import { useEffect, useMemo, useRef, useState } from "react";
import { Route, Trash2 } from "lucide-react";
import { ContextMenu, ContextMenuRow } from "./ContextMenu";
import { routeToSystem } from "../api/chains";
import { useStaticSystemsById } from "../hooks/useStaticSystems";
import { useChainStore } from "../store/chainStore";
import { routeTarget } from "../utils/routeTarget";

interface SystemContextMenuProps {
  systemId: string;
  x: number;
  y: number;
  onClose: () => void;
}

export function SystemContextMenu({
  systemId,
  x,
  y,
  onClose,
}: SystemContextMenuProps) {
  const removeSystem = useChainStore((s) => s.removeSystem);
  const rootSystemId = useChainStore((s) => s.rootSystemId);
  const chainId = useChainStore((s) => s.chainId);
  const connections = useChainStore((s) => s.connections);
  const { byId: staticById } = useStaticSystemsById();
  const isRoot = systemId === rootSystemId;

  const target = useMemo(
    () => routeTarget(staticById, connections, systemId),
    [staticById, connections, systemId],
  );

  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routing, setRouting] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const onClick = async () => {
    if (busy || isRoot) return;
    if (!armed) {
      setError(null);
      setArmed(true);
      timerRef.current = window.setTimeout(() => {
        setArmed(false);
        timerRef.current = null;
      }, 3000);
      return;
    }
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setBusy(true);
    try {
      await removeSystem(systemId);
      onClose();
    } catch (err) {
      setArmed(false);
      setError(err instanceof Error ? err.message : "Failed to remove system.");
    } finally {
      setBusy(false);
    }
  };

  const onRoute = async () => {
    if (routing || target == null || !chainId) return;
    setRouteError(null);
    setRouting(true);
    try {
      await routeToSystem(chainId, target);
      onClose();
    } catch (err) {
      setRouteError(
        err instanceof Error ? err.message : "Failed to set route.",
      );
    } finally {
      setRouting(false);
    }
  };

  const label = isRoot
    ? "Remove system (root)"
    : busy
      ? "Removing…"
      : armed
        ? "Click again to confirm"
        : "Remove system";

  return (
    <ContextMenu x={x} y={y} onClose={onClose}>
      <ContextMenuRow
        onClick={onRoute}
        disabled={target == null || routing || !chainId}
      >
        <Route size={14} aria-hidden />
        {routing ? "Routing…" : "Route to system"}
      </ContextMenuRow>
      {routeError && (
        <p className="form__hint form__hint--error" role="alert">
          {routeError}
        </p>
      )}
      <ContextMenuRow onClick={onClick} danger disabled={busy || isRoot}>
        <Trash2 size={14} aria-hidden />
        {label}
      </ContextMenuRow>
      {error && (
        <p className="form__hint form__hint--error" role="alert">
          {error}
        </p>
      )}
    </ContextMenu>
  );
}
